import React, { ReactElement, useState } from 'react'
import { PrimaryButton, SecondaryButton } from './Button'
import Radom from '../state/Radom'
import { InputLabel } from './Input'
import { RADOM_COLORS } from '../util/Constants'
import Checkbox from './Checkbox'
import styled from 'styled-components'
import { endOfDay, endOfMonth, intlFormat, startOfDay, startOfMonth, subDays, subMonths } from 'date-fns'
import { errorToast, getExportFileName } from '../util/Util'
import DateSelector from './DateSelector'
import { SummaryLabel, SummaryMeta } from './DashboardPrimitives'

const DateRange = styled(SecondaryButton)<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  color: ${({ $selected }) => $selected ? '#20242d' : RADOM_COLORS.BLACK};
  border-color: ${({ $selected }) => $selected ? '#d7dbe3' : '#e7ebf2'};
  background: ${({ $selected }) => $selected ? '#f7f7f8' : '#fff'};
  border-radius: 14px;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Layout = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.2fr) minmax(220px, 0.8fr);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

const RangeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const PeriodList = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
`

const PeriodButton = styled(SecondaryButton)<{ $selected: boolean }>`
  border-color: ${({ $selected }) => $selected ? '#d7dbe3' : '#e7ebf2'};
  background: ${({ $selected }) => $selected ? '#f7f7f8' : '#fff'};
  color: ${({ $selected }) => $selected ? '#20242d' : '#313745'};
  border-radius: 14px;
  justify-content: flex-start;
  min-height: 46px;
  padding: 10px 14px;
  width: 100%;
`

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PreviewCard = styled.div`
  background: #fafbfc;
  border: 1px solid #e8edf5;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
`

const Footer = styled.div`
  border-top: 1px solid #edf1f7;
  margin-top: 4px;
  padding-top: 18px;
`

const CustomDateRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 12px 0;

  @media (max-width: 760px) {
    align-items: stretch;
    flex-direction: column;
  }
`

const AppliedRangeNote = styled.div`
  border-top: 1px solid #e8edf5;
  color: #6b7280;
  font-size: 12.5px;
  line-height: 1.45;
  margin-top: 2px;
  padding-top: 12px;
`

const EXPORT_TIME_RANGES = [
  {
    label: 'Today',
    dateFn: () => {
      return {
        from: startOfDay(new Date()),
        to: new Date()
      }
    }
  },
  {
    label: 'Current month',
    dateFn: () => {
      return {
        from: startOfMonth(new Date()),
        to: endOfDay(new Date())
      }
    }
  },
  {
    label: 'Last 7 days',
    dateFn: () => {
      return {
        from: startOfDay(subDays(new Date(), 7)),
        to: endOfDay(new Date())
      }
    }
  },
  {
    label: 'Last month',
    dateFn: () => {
      return {
        from: startOfMonth(subMonths(new Date(), 1)),
        to: endOfMonth(subMonths(new Date(), 1))
      }
    }
  },
  {
    label: 'Last 3 months',
    dateFn: () => {
      return {
        from: startOfMonth(subMonths(new Date(), 2)),
        to: endOfMonth(new Date())
      }
    }
  },
  {
    label: 'All',
    dateFn: () => {
      return {
        from: new Date(0),
        to: new Date()
      }
    }
  },
  { label: 'Custom' }
]

function downloadBlob(blob: Blob, filename: string): HTMLAnchorElement {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')

  a.href = url
  a.download = filename || 'download'

  const clickHandler = (): void => {
    setTimeout(() => {
      URL.revokeObjectURL(url)
      removeEventListener('click', clickHandler)
    }, 150)
  }

  a.addEventListener('click', clickHandler, false)
  a.click()

  return a
}

const ExportBalanceForm = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = useState(EXPORT_TIME_RANGES[0])

  const [customFromDate, setCustomFromDate] = useState<Date | undefined>(undefined)
  const [customToDate, setCustomToDate] = useState<Date | undefined>(undefined)

  const [period, setPeriod] = useState('daily')

  const selectedRangePreview = selectedTimeRange.label === 'Custom'
    ? customFromDate && customToDate
      ? `${intlFormat(customFromDate)} - ${intlFormat(customToDate)}`
      : 'Choose a custom start and end date'
    : selectedTimeRange.dateFn
      ? `${intlFormat(selectedTimeRange.dateFn().from)} - ${intlFormat(selectedTimeRange.dateFn().to)}`
      : 'Choose a custom start and end date'

  const exportBalance = async (): Promise<void> => {
    let fromDate, toDate
    if (selectedTimeRange.label === 'Custom') {
      if (customFromDate && customToDate) {
        fromDate = startOfDay(customFromDate).toISOString()
        toDate = endOfDay(customToDate).toISOString()
      } else {
        errorToast('Must select from and to custom date ranges')
        return
      }
    } else if (selectedTimeRange.dateFn) {
      fromDate = selectedTimeRange.dateFn().from.toISOString()
      toDate = selectedTimeRange.dateFn().to.toISOString()
    }
    setIsLoading(true)

    Radom.exportBalance(
      fromDate,
      toDate,
      period
    )
      .then(data => {
        const blob = new Blob(
          [data],
          { type: 'text/csv' }
        )

        const fileName = getExportFileName('balance')

        downloadBlob(blob, fileName)
      })
      .catch(err => {
        errorToast(err.response?.data?.error || err.message)
      }).finally(() => {
        setIsLoading(false)
      })
  }

  return <>
    <div style={{
      width: 'min(760px, 100%)',
      display: 'flex',
      flexDirection: 'column',
      gap: 18,
      fontSize: 14
    }}>
      <Layout>
        <Section>
          <SectionHeading>
            <InputLabel>Date range</InputLabel>
          </SectionHeading>
          <RangeList>
            {EXPORT_TIME_RANGES.map((t) => {
              const isSelected = selectedTimeRange === t

              return (
                <div key={t.label} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <DateRange $selected={isSelected} onClick={() => setSelectedTimeRange(t)} disabled={isLoading}>
                    <Checkbox isRadio checked={isSelected} size={18} disabled={isLoading} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                      <div style={{ alignItems: 'center', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span>{t.label}</span>
                      </div>
                    </div>
                  </DateRange>
                  {selectedTimeRange.label === 'Custom' && t.label === 'Custom' &&
                    <CustomDateRow>
                      <DateSelector onChange={d => setCustomFromDate(d)} value={customFromDate} max={customToDate} placeholder='Start date' />
                      <span style={{ color: '#7d8492', fontSize: 13 }}>to</span>
                      <DateSelector onChange={d => setCustomToDate(d)} value={customToDate} min={customFromDate} placeholder='End date' />
                    </CustomDateRow>}
                </div>
              )
            })}
          </RangeList>
        </Section>

        <Section>
          <SectionHeading>
            <InputLabel>Grouping</InputLabel>
          </SectionHeading>
          <PreviewCard>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <SummaryLabel>Report rows</SummaryLabel>
              <SummaryMeta>Choose whether the export creates one row per day, week, or month.</SummaryMeta>
            </div>
            <PeriodList>
              {['Daily', 'Weekly', 'Monthly'].map(p =>
                <PeriodButton
                  key={p}
                  $selected={p.toLowerCase() === period}
                  onClick={() => setPeriod(p.toLowerCase())}
                  disabled={isLoading}>
                  <Checkbox isRadio checked={p.toLowerCase() === period} size={16} disabled={isLoading} />
                  <span>{p}</span>
                </PeriodButton>)}
            </PeriodList>
            <AppliedRangeNote>
              Applies to {selectedRangePreview}
            </AppliedRangeNote>
          </PreviewCard>
        </Section>
      </Layout>
    </div>
    <Footer>
      <PrimaryButton
        style={{ width: '100%' }}
        onClick={exportBalance}
        disabled={selectedTimeRange.label === 'Custom' && (!customFromDate || !customToDate)}
        isLoading={isLoading}>
      Export balance report
      </PrimaryButton>
    </Footer>
  </>
}

export default ExportBalanceForm
